/**
 * Location classifier — interpret existing free-text location into one of:
 *   - "exact"   → precise place/address (default for non-matching strings)
 *   - "general" → soft area (e.g., "north field", "the park")
 *   - "private" → hidden until joined (e.g., "home", "my place", "backyard")
 *
 * No new input UI; the user types whatever they type, and we render
 * intent-aware output + map behavior on Activity Detail. Calm, opinionated,
 * never aggressive — when in doubt, treat as exact.
 */

const PRIVATE_PATTERNS = [
  /\bhome\b/i,
  /\bmy\s+place\b/i,
  /\bmy\s+house\b/i,
  /\bmy\s+apartment\b/i,
  /\bmy\s+yard\b/i,
  /\bbackyard\b/i,
  /\bback\s+yard\b/i,
  /\bfront\s+yard\b/i,
  /\bgarage\b/i,
  /\bbasement\b/i,
  /\bliving\s+room\b/i,
  /\bdining\s+room\b/i,
  /\bdriveway\b/i,
  /\bporch\b/i,
  /\bbalcony\b/i,
  /\bprivate\b/i,
];

const GENERAL_HINT_WORDS = [
  'field', 'meadow', 'park', 'beach', 'trail', 'forest', 'woods',
  'lake', 'pond', 'river', 'creek', 'lot', 'green', 'commons',
  'square', 'corner', 'pavilion', 'grove', 'hill', 'ridge', 'flats',
];

const GENERAL_DIRECTIONALS = /\b(north|south|east|west|upper|lower|new|old|big|little)\b/i;

const NUMERIC_ADDRESS = /\d+\s+\w+/;

export function classifyLocation(raw) {
  const text = (typeof raw === 'string' ? raw : raw?.name || '').trim();
  if (!text) return 'exact';

  // PRIVATE wins outright. People rarely type "home" by accident.
  for (const pat of PRIVATE_PATTERNS) {
    if (pat.test(text)) return 'private';
  }

  // EXACT signals: contains a numeric street address, comma, or "&" / "and"
  // (intersection style) → treat as a specific place.
  if (NUMERIC_ADDRESS.test(text)) return 'exact';
  if (/[,]/.test(text)) return 'exact';
  if (/\b\d{2,}/.test(text)) return 'exact';

  // GENERAL signals: short text containing a generic outdoor/area word, or
  // starts with a directional ("North Field", "East Beach"). No commas.
  const lower = text.toLowerCase();
  const tokens = lower.split(/\s+/).filter(Boolean);
  const hasHint = GENERAL_HINT_WORDS.some(w => tokens.includes(w));
  const startsDirectional = GENERAL_DIRECTIONALS.test(tokens[0] || '');

  if (hasHint && tokens.length <= 4) return 'general';
  if (startsDirectional && hasHint) return 'general';

  // Default: exact. Most named places fall here ("Central Park", "Penelope's
  // Café", "Memorial Hall") — they should still resolve to a precise pin.
  return 'exact';
}

export function locationDisplayLabel(raw) {
  const text = (typeof raw === 'string' ? raw : raw?.name || '').trim();
  const kind = classifyLocation(text);
  if (kind === 'private') return 'Location shared after joining';
  if (kind === 'general') return `${text} area`;
  return text;
}
